import api from '@/shared/api';
import { extractApiErrorMessage } from '@/shared/lib';
import type { AuditUserResponse, SortOrder } from '@/shared/lib';
import type {
  DirectoryFolderContentType,
  DirectoryFolderType,
  DirectoryIconId,
  DirectoryNodePermissions,
  DirectoryPermissionTargetType,
} from '../model/types';

export const MODULE_ALREADY_EXISTS_MESSAGE = 'Já existe um módulo com este título';
export const MODULE_ALREADY_EXISTS_CODE = 'MODULE_ALREADY_EXISTS';

export interface ModuleListItem {
  id: number;
  name: string;
}

export type DirectoryLinkedItemType = 'DOCUMENT' | 'RECORD' | 'MATRIX';

/**
 * `GENERATED_RECORD` is derived from the folder's matrix revision and has no link, so it can never
 * be moved or removed. `RECORD` is a document revision of the registration type.
 */
export type DirectoryItemType = DirectoryLinkedItemType | 'GENERATED_RECORD';

export interface DirectoryItemResponse {
  linkId: number | null;
  derived?: true;
  itemType: DirectoryItemType;
  /** Id da REVISÃO vinculada para documentos/registros, e da matriz para matrizes. */
  itemId: number;
  name: string | null;
  /** Document or matrix prefix (`RQ50.10`). It is the label shown when the title is missing. */
  prefix?: string | null;
  /** Número da revisão vinculada. Ausente para matrizes. */
  revision?: number | null;
  archived?: boolean;
  createdAt: string | null;
  createdBy: AuditUserResponse;
  savedInDirectoryBy: AuditUserResponse;
}

export interface FolderNodeResponse {
  id: number;
  name: string;
  type: DirectoryFolderType;
  contentType?: DirectoryFolderContentType;
  prefixId?: number | null;
  prefixCode?: string | null;
  createdAt: string | null;
  createdBy: AuditUserResponse;
  savedInDirectoryBy: AuditUserResponse;
  matrixPlacementLocked?: boolean;
  matrixPlacementMoveLocked?: boolean;
  matrixPlacementDeleteLock?: 'QUALITY_RECORDS_ROOT' | 'MATRIX_FOLDER' | null;
  children: FolderNodeResponse[];
  items?: DirectoryItemResponse[];
}

export interface ModuleWithFoldersResponse {
  moduleId: number;
  moduleName: string;
  iconId: string | null;
  createdAt: string | null;
  createdBy: AuditUserResponse;
  savedInDirectoryBy: AuditUserResponse;
  holdsMatrixLinks?: boolean;
  folders: FolderNodeResponse[];
  items?: DirectoryItemResponse[];
}

export interface CreatedModuleResponse {
  id: number;
  name: string;
  iconId: string | null;
  createdAt?: string | null;
}

export interface CreateModulePayload {
  name: string;
  iconId?: DirectoryIconId;
}

export type CreateModuleResult =
  | { success: true; data: CreatedModuleResponse }
  | { success: false; duplicate: true }
  | { success: false; duplicate: false };

interface ApiErrorResponse {
  message?: string;
  errorCode?: string;
}

function getApiErrorData(error: unknown): ApiErrorResponse | undefined {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: ApiErrorResponse } }).response?.data === 'object'
  ) {
    return (error as { response: { data: ApiErrorResponse } }).response.data;
  }
  return undefined;
}

export function isModuleDuplicateError(error: unknown): boolean {
  const data = getApiErrorData(error);
  if (!data) {
    return false;
  }
  if (data.errorCode === MODULE_ALREADY_EXISTS_CODE) {
    return true;
  }
  return data.message === MODULE_ALREADY_EXISTS_MESSAGE;
}

export async function listModules(): Promise<ModuleListItem[]> {
  const response = await api.get<{ data: ModuleListItem[] }>('/modules');
  return response.data.data ?? [];
}

export type ModuleTreeSortField = 'name' | 'createdAt';
export type ModuleTreeSortOrder = SortOrder;

export async function getModulesWithFolders(
  sort?: ModuleTreeSortField,
  order?: ModuleTreeSortOrder
): Promise<ModuleWithFoldersResponse[]> {
  const response = await api.get<{ data: ModuleWithFoldersResponse[] }>('/modules/with-folders', {
    params: { sort, order },
  });
  return response.data.data ?? [];
}

export async function createModule(payload: CreateModulePayload): Promise<CreateModuleResult> {
  try {
    const response = await api.post<{ data: CreatedModuleResponse }>('/modules', payload);
    return { success: true, data: response.data.data };
  } catch (error) {
    if (isModuleDuplicateError(error)) {
      return { success: false, duplicate: true };
    }
    return { success: false, duplicate: false };
  }
}

export interface UpdateModulePayload {
  name: string;
  iconId?: DirectoryIconId;
}

export type UpdateDirectoryNodeResult =
  { success: true } | { success: false; duplicate: boolean; message?: string };

export async function updateModule(
  moduleId: number,
  payload: UpdateModulePayload
): Promise<UpdateDirectoryNodeResult> {
  try {
    await api.put(`/modules/${moduleId}`, payload);
    return { success: true };
  } catch (error) {
    return { success: false, duplicate: isModuleDuplicateError(error) };
  }
}

export const FOLDER_ALREADY_EXISTS_MESSAGE =
  'Já existe uma pasta com este nome no diretório informado';
export const FOLDER_ALREADY_EXISTS_CODE = 'FOLDER_ALREADY_EXISTS';
export const MATRIX_PLACEMENT_LOCKED_CODE = 'MATRIX_PLACEMENT_LOCKED';

export interface CreatedFolderResponse {
  id: number;
  name: string;
  type: 'CUSTOM';
  moduleId: number;
  parentId: number | null;
  createdAt?: string | null;
}

export interface CreateFolderPayload {
  moduleId: number;
  parentId?: number | null;
  name: string;
}

export type CreateFolderResult =
  | { success: true; data: CreatedFolderResponse }
  | { success: false; duplicate: true }
  | { success: false; duplicate: false };

export function isFolderDuplicateError(error: unknown): boolean {
  const data = getApiErrorData(error);
  if (!data) {
    return false;
  }
  if (data.errorCode === FOLDER_ALREADY_EXISTS_CODE) {
    return true;
  }
  return data.message === FOLDER_ALREADY_EXISTS_MESSAGE;
}

export async function createFolder(payload: CreateFolderPayload): Promise<CreateFolderResult> {
  try {
    const response = await api.post<{ data: CreatedFolderResponse }>('/folders', payload);
    return { success: true, data: response.data.data };
  } catch (error) {
    if (isFolderDuplicateError(error)) {
      return { success: false, duplicate: true };
    }
    return { success: false, duplicate: false };
  }
}

export interface UpdateFolderPayload {
  name: string;
}

export async function updateFolder(
  folderId: number,
  payload: UpdateFolderPayload
): Promise<UpdateDirectoryNodeResult> {
  try {
    await api.put(`/folders/${folderId}`, payload);
    return { success: true };
  } catch (error) {
    const data = getApiErrorData(error);
    if (data?.errorCode === MATRIX_PLACEMENT_LOCKED_CODE && data.message) {
      return { success: false, duplicate: false, message: data.message };
    }
    return { success: false, duplicate: isFolderDuplicateError(error) };
  }
}

export async function updateDirectoryMatrixArchive(
  matrixId: number,
  archived: boolean
): Promise<UpdateDirectoryNodeResult> {
  try {
    await api.patch(`/directories/matrices/${matrixId}/archive`, { archived });
    return { success: true };
  } catch (error) {
    return { success: false, duplicate: false, message: extractApiErrorMessage(error) };
  }
}

export type MoveItemType = 'FOLDER' | 'DOCUMENT' | 'RECORD' | 'MATRIX';

export interface MoveItem {
  itemType: MoveItemType;
  /**
   * A entidade movida: a pasta para FOLDER, a matriz para MATRIX e a **revisão** para
   * DOCUMENT/RECORD — o mesmo `itemId` que a árvore expõe.
   */
  itemId: number;
  linkId?: number;
}

export interface MoveDestination {
  folderId?: number;
  moduleId?: number;
}

export interface MoveDirectoryItemsPayload {
  items: MoveItem[];
  destination: MoveDestination;
}

export type MoveDirectoryItemsResult = { success: true } | { success: false; message?: string };

export async function moveDirectoryItems(
  payload: MoveDirectoryItemsPayload
): Promise<MoveDirectoryItemsResult> {
  try {
    await api.post('/directories/move', payload);
    return { success: true };
  } catch (error) {
    return { success: false, message: extractApiErrorMessage(error) };
  }
}

export interface RemoveDirectoryItem {
  itemType: DirectoryLinkedItemType;
  /** A entidade removida: a **revisão** para DOCUMENT/RECORD, a matriz para MATRIX. */
  itemId: number;
  linkId?: number;
}

export type DeleteModuleResult = { success: true } | { success: false; message?: string };
export type DeleteFolderResult = { success: true } | { success: false; message?: string };
export type RemoveDirectoryItemsResult = { success: true } | { success: false; message?: string };

export async function deleteModule(id: number): Promise<DeleteModuleResult> {
  try {
    await api.delete(`/modules/${id}`);
    return { success: true };
  } catch (error) {
    return { success: false, message: extractApiErrorMessage(error) };
  }
}

export async function deleteFolder(id: number): Promise<DeleteFolderResult> {
  try {
    await api.delete(`/folders/${id}`);
    return { success: true };
  } catch (error) {
    return { success: false, message: extractApiErrorMessage(error) };
  }
}

export async function removeDirectoryItems(
  items: RemoveDirectoryItem[]
): Promise<RemoveDirectoryItemsResult> {
  try {
    await api.post('/directories/items/remove', { items });
    return { success: true };
  } catch (error) {
    return { success: false, message: extractApiErrorMessage(error) };
  }
}

export interface DirectoryPermissionsTarget {
  targetType: DirectoryPermissionTargetType;
  targetId: number;
}

export type GetDirectoryPermissionsResult =
  { success: true; data: DirectoryNodePermissions } | { success: false };

export type UpdateDirectoryPermissionsResult =
  { success: true; data: DirectoryNodePermissions } | { success: false };

const DIRECTORY_PERMISSIONS_REQUEST_TIMEOUT_MS = 10000;

export async function getDirectoryPermissions(
  target: DirectoryPermissionsTarget
): Promise<GetDirectoryPermissionsResult> {
  try {
    const response = await api.get<{ data: DirectoryNodePermissions }>(
      `/directories/${target.targetType}/${target.targetId}/permissions`
    );
    return { success: true, data: response.data.data };
  } catch {
    return { success: false };
  }
}

export interface BulkDirectoryPermissionSections {
  positionIds?: number[];
  functionalActivityIds?: number[];
}

export async function bulkUpdateDirectoryPermissions(
  targets: DirectoryPermissionsTarget[],
  sections: BulkDirectoryPermissionSections
): Promise<UpdateDirectoryPermissionsResult> {
  try {
    const response = await api.patch<{ data: DirectoryNodePermissions }>(
      '/directories/permissions/bulk',
      { targets, ...sections },
      { timeout: DIRECTORY_PERMISSIONS_REQUEST_TIMEOUT_MS }
    );
    return { success: true, data: response.data.data };
  } catch {
    return { success: false };
  }
}

export async function updateDirectoryPositionPermissions(
  target: DirectoryPermissionsTarget,
  positionIds: number[]
): Promise<UpdateDirectoryPermissionsResult> {
  try {
    const response = await api.patch<{ data: DirectoryNodePermissions }>(
      `/directories/${target.targetType}/${target.targetId}/permissions/positions`,
      { positionIds },
      { timeout: DIRECTORY_PERMISSIONS_REQUEST_TIMEOUT_MS }
    );
    return { success: true, data: response.data.data };
  } catch {
    return { success: false };
  }
}

export async function updateDirectoryFunctionalActivityPermissions(
  target: DirectoryPermissionsTarget,
  functionalActivityIds: number[]
): Promise<UpdateDirectoryPermissionsResult> {
  try {
    const response = await api.patch<{ data: DirectoryNodePermissions }>(
      `/directories/${target.targetType}/${target.targetId}/permissions/functional-activities`,
      { functionalActivityIds },
      { timeout: DIRECTORY_PERMISSIONS_REQUEST_TIMEOUT_MS }
    );
    return { success: true, data: response.data.data };
  } catch {
    return { success: false };
  }
}
